import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { useI18n } from "@/i18n";
import { useSeo } from "@/lib/seo";
import { BRAND } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/posts/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setPost)
      .catch(() => setNotFound(true));
  }, [slug]);

  const url = `https://dmo-tech.ro/blog/${slug}`;
  useSeo({
    title: post ? `${post.title} — DMO Technology` : "DMO Technology",
    description: post?.excerpt,
    image: post?.cover,
    url,
    jsonLd: post
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          image: post.cover,
          datePublished: post.date,
          author: { "@type": "Organization", name: "DMO Technology" },
          publisher: { "@type": "Organization", name: "DMO Technology" },
          mainEntityOfPage: url,
          keywords: (post.tags || []).join(", "),
        }
      : null,
  });

  if (notFound) {
    return (
      <main className="grid min-h-screen place-items-center bg-ink-900 px-6 text-center">
        <div>
          <p className="font-display text-3xl font-bold text-white">404</p>
          <p className="mt-2 font-body text-white/55">Articolul nu a fost găsit.</p>
          <Link to="/blog" className="mt-6 inline-block font-mono text-xs uppercase tracking-widest text-cyan-neon">← {t("blog.back")}</Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return <main className="grid min-h-screen place-items-center bg-ink-900"><span className="font-mono text-sm text-white/40">…</span></main>;
  }

  return (
    <main className="relative min-h-screen bg-ink-900 pt-28" data-testid="blogpost">
      <article className="relative mx-auto max-w-3xl px-5 py-12 sm:px-8">
        <Link to="/blog" data-testid="blogpost-back" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-white/50 transition-colors hover:text-cyan-neon">
          <ArrowLeft size={14} /> {t("blog.back")}
        </Link>

        <span className="mt-8 inline-block rounded-full border border-cyan-neon/30 bg-cyan-neon/5 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-neon">{post.category}</span>
        <h1 className="mt-5 font-display text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl">{post.title}</h1>

        <div className="mt-5 flex flex-wrap items-center gap-5 font-mono text-[11px] uppercase tracking-widest text-white/40">
          <span className="flex items-center gap-1.5"><Calendar size={13} /> {new Date(post.date).toLocaleDateString("ro-RO", { year: "numeric", month: "long", day: "numeric" })}</span>
          <span className="flex items-center gap-1.5"><Clock size={13} /> {post.read_min} {t("blog.read")}</span>
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
          <img src={post.cover} alt={post.title} className="h-64 w-full object-cover sm:h-80" />
        </div>

        <div className="blog-body mt-10" dangerouslySetInnerHTML={{ __html: post.body }} />

        {post.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2 border-t border-white/10 pt-8">
            {post.tags.map((tag) => (
              <span key={tag} className="border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white/45">{tag}</span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="card-edge mt-12 rounded-2xl p-8 text-center">
          <h3 className="font-display text-2xl font-bold text-white">{t("blog.cta.t")}</h3>
          <p className="mx-auto mt-3 max-w-md font-body text-sm leading-relaxed text-white/55">{t("blog.cta.b")}</p>
          <Link to="/#contact" data-testid="blogpost-cta" className="mt-6 inline-block bg-cyan-neon px-7 py-3.5 font-mono text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-white">
            {t("blog.cta.btn")}
          </Link>
        </div>
      </article>
    </main>
  );
}
